<template>
  <div class="card fila-folhetos-card">
    <div class="card-header pb-0 d-flex align-items-center justify-content-between flex-wrap gap-2">
      <div>
        <h6 class="mb-0">Fila de Folhetos</h6>
        <p class="text-sm text-secondary mb-0">Produtos escaneados aguardando processamento</p>
      </div>
      <button class="btn-refresh" @click="load" :disabled="loading" title="Atualizar">
        <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
      </button>
    </div>

    <div class="card-body">

      <!-- Select de supermercado + validade -->
      <div class="supermarket-bar mb-4 d-flex align-items-end gap-3 flex-wrap">
        <div class="flex-grow-1">
          <label class="form-label-custom">
            <i class="fas fa-store me-1"></i>Supermercado
          </label>
          <div v-if="loadingSupermercados" class="d-flex align-items-center gap-2">
            <div class="spinner-border spinner-border-sm spinner-teal"></div>
            <span class="text-sm text-secondary">Carregando...</span>
          </div>
          <select v-else v-model="selectedSupermercadoId" class="form-select form-select-custom">
            <option value="">Selecione um supermercado...</option>
            <option v-for="s in supermercados" :key="s.id" :value="s.id">
              {{ s.nome || s.name }}
            </option>
          </select>
        </div>
        <div>
          <label class="form-label-custom">
            <i class="fas fa-calendar-alt me-1"></i>Validade do preço
          </label>
          <input
            type="date"
            v-model="expiresAt"
            class="form-control form-select-custom"
            style="min-width: 160px;"
            title="Data de validade do preço (opcional)"
          />
        </div>
      </div>

      <!-- Carregando -->
      <div v-if="loading" class="d-flex align-items-center justify-content-center py-5 gap-3">
        <div class="spinner-border spinner-teal"></div>
        <span class="text-secondary">Carregando...</span>
      </div>

      <!-- Erro -->
      <div v-else-if="error" class="alert alert-danger">
        <i class="fas fa-exclamation-triangle me-2"></i>{{ error }}
      </div>

      <!-- Vazio -->
      <div v-else-if="items.length === 0" class="empty-state">
        <i class="fas fa-inbox"></i>
        <p class="mt-2 mb-0 text-secondary">Nenhum produto pendente na fila</p>
      </div>

      <!-- Grid -->
      <div v-else class="products-grid">
        <div v-for="item in items" :key="item.id" class="pcard">

          <!-- Excluir -->
          <button
            class="pcard__delete-btn"
            :disabled="deletingIds.has(item.id)"
            @click="confirmDelete(item)"
            title="Excluir produto da fila"
          >
            <i class="fas fa-times"></i>
          </button>

          <!-- Zona 1: imagem -->
          <div class="pcard__thumb">
            <img
              v-if="item.gtin && (item.image || item.image_url)"
              :src="item.image || item.image_url"
              :alt="item.name"
              @error="$event.target.style.display='none'"
            />
            <i v-else class="fas fa-box-open pcard__no-img"></i>
          </div>

          <!-- Zona 2: info -->
          <div class="pcard__info">
            <p class="pcard__name">{{ item.description || item.name || '—' }}</p>
            <div class="pcard__meta">
              <span class="pcard__brand">{{ item.brand || '—' }}</span>
              <span class="pcard__dot"></span>
              <span class="pcard__weight">{{ item.weight || '—' }}</span>
            </div>
            <div class="pcard__gtin">
              <i class="fas fa-barcode"></i>
              <span>{{ item.gtin || 'Sem GTIN' }}</span>
            </div>
          </div>

          <!-- Zona 3: preço -->
          <div class="pcard__price-zone">
            <span class="pcard__price-label">Preço</span>
            <div class="pcard__price-input" :class="{ 'pcard__price-input--invalid': !hasValidPrice(item) }">
              <span class="pcard__currency">R$</span>
              <input
                type="number"
                v-model="item.editPrice"
                min="0"
                step="0.01"
                placeholder="0,00"
              />
            </div>
            <p v-if="!hasValidPrice(item)" class="pcard__price-warning">
              <i class="fas fa-exclamation-circle me-1"></i>Adicione um valor para o produto
            </p>
          </div>

          <!-- Zona 4: rodapé -->
          <div class="pcard__footer">
            <span class="pcard__date">{{ formatDate(item.created_at) }}</span>
            <div class="pcard__actions">
              <button
                class="pcard__redo-btn"
                @click="openRefazerModal(item)"
                :title="!hasValidPrice(item) ? 'Informe o preço do produto' : 'Buscar GTIN do produto'"
                :disabled="!selectedSupermercadoId || !hasValidPrice(item)"
              >
                <i class="fas fa-search me-1"></i>Buscar GTIN
              </button>
              <button
                class="pcard__find-btn"
                @click="openBuscarProdutoModal(item)"
                :title="!hasValidPrice(item) ? 'Informe o preço do produto' : 'Buscar produto cadastrado'"
                :disabled="!selectedSupermercadoId || !hasValidPrice(item)"
              >
                <i class="fas fa-box me-1"></i>Buscar produto
              </button>
              <button
                class="pcard__save-btn"
                :class="{ saving: savingIds.has(item.id) }"
                :disabled="!selectedSupermercadoId || !item.gtin || !hasValidPrice(item) || savingIds.has(item.id)"
                @click="saveProduct(item)"
              >
                <span v-if="savingIds.has(item.id)">
                  <i class="fas fa-circle-notch fa-spin"></i>
                </span>
                <span v-else>
                  <i class="fas fa-check me-1"></i>Salvar
                </span>
              </button>
            </div>
          </div>

        </div>
      </div>

      <!-- Total -->
      <div v-if="items.length > 0" class="footer-info">
        <span class="text-xs text-secondary">
          <i class="fas fa-layer-group me-1"></i>{{ items.length }} produto(s) na fila
        </span>
      </div>

    </div>
  </div>

  <!-- Modal Refazer GTIN -->
  <teleport to="body">
    <div v-if="modalRefazer" class="modal-overlay" @click.self="modalRefazer = false">
      <div class="modal-box">
        <div class="modal-box__header">
          <h6 class="mb-0"><i class="fas fa-search me-2"></i>Buscar GTIN</h6>
          <button class="modal-close" @click="modalRefazer = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-box__body">
          <div v-if="loadingTokens" class="d-flex align-items-center justify-content-center py-3 gap-2">
            <div class="spinner-border spinner-border-sm spinner-teal"></div>
            <span class="text-sm text-secondary">Carregando tokens...</span>
          </div>
          <div v-else-if="modalTokens.length === 0" class="text-center text-secondary py-3">
            <i class="fas fa-coins mb-2" style="font-size:1.5rem; color:#cbd5e0;"></i>
            <p class="mb-0 text-sm">Nenhum token disponível</p>
          </div>
          <div v-else class="tokens-list">
            <label
              v-for="t in modalTokens"
              :key="t.id"
              class="token-row"
              :class="{ 'token-row--selected': selectedToken === t }"
            >
              <input
                type="radio"
                :value="t"
                v-model="selectedToken"
                class="token-radio"
              />
              <span class="token-name">{{ t.token }}</span>
              <span class="token-qty">{{ t.usage_count }}</span>
            </label>
          </div>
        </div>
        <div class="modal-box__footer">
          <button
            class="btn-buscar-gtin"
            :disabled="!selectedToken || searchingGtin"
            @click="buscarGtin"
          >
            <span v-if="searchingGtin">
              <i class="fas fa-circle-notch fa-spin me-1"></i>Buscando...
            </span>
            <span v-else>
              <i class="fas fa-search me-1"></i>Buscar
            </span>
          </button>
        </div>
      </div>
    </div>
  </teleport>

  <!-- Modal Resultado Cosmos -->
  <teleport to="body">
    <div v-if="modalResultado" class="modal-overlay" @click.self="recusarProduto">
      <div class="modal-box modal-box--resultado">
        <div class="modal-box__header">
          <h6 class="mb-0"><i class="fas fa-box-open me-2"></i>Produtos Encontrados</h6>
          <button class="modal-close" @click="recusarProduto">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-box__body">
          <p class="cosmos-count">{{ cosmosProducts.length }} produto(s) encontrado(s) — selecione o correto</p>
          <div class="cosmos-list">
            <label
              v-for="p in cosmosProducts"
              :key="p.gtin"
              class="cosmos-row"
              :class="{ 'cosmos-row--selected': selectedCosmosProduct === p }"
            >
              <input type="radio" :value="p" v-model="selectedCosmosProduct" class="cosmos-radio" />
              <img
                v-if="p.barcode_image"
                :src="p.barcode_image"
                class="cosmos-barcode-img"
                :alt="p.description"
                @error="$event.target.style.display='none'"
              />
              <div class="cosmos-row-info">
                <span class="cosmos-row-desc">{{ p.description }}</span>
                <span class="cosmos-row-gtin">
                  <i class="fas fa-barcode me-1"></i>{{ p.gtin }}
                  <button
                    class="cosmos-copy-btn"
                    @click.prevent.stop="copyGtin(p.gtin)"
                    :title="'Copiar GTIN ' + p.gtin"
                  >
                    <i class="fas fa-copy"></i>
                  </button>
                </span>
              </div>
            </label>
          </div>
        </div>
        <div class="modal-box__footer modal-box__footer--resultado">
          <button class="btn-recusar" @click="recusarProduto" :disabled="savingNewProduct">
            <i class="fas fa-times me-1"></i>Recusar
          </button>
          <button class="btn-salvar-cosmos" @click="salvarNovoProduto" :disabled="savingNewProduct || !selectedCosmosProduct">
            <span v-if="savingNewProduct">
              <i class="fas fa-circle-notch fa-spin me-1"></i>Salvando...
            </span>
            <span v-else>
              <i class="fas fa-check me-1"></i>Salvar
            </span>
          </button>
        </div>
      </div>
    </div>
  </teleport>

  <!-- Modal Buscar Produto Cadastrado -->
  <teleport to="body">
    <div v-if="modalBuscarProduto" class="modal-overlay" @click.self="closeBuscarProduto">
      <div class="modal-box modal-box--resultado">
        <div class="modal-box__header">
          <h6 class="mb-0"><i class="fas fa-box me-2"></i>Buscar Produto</h6>
          <button class="modal-close" @click="closeBuscarProduto">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-box__body">
          <!-- Resumo do que será salvo -->
          <div class="busca-resumo">
            <span class="busca-resumo__item">
              <i class="fas fa-store me-1"></i>{{ selectedSupermercadoNome || '—' }}
            </span>
            <span class="busca-resumo__item busca-resumo__price">
              <i class="fas fa-tag me-1"></i>R$ {{ buscarProdutoItem && buscarProdutoItem.editPrice ? buscarProdutoItem.editPrice : '0,00' }}
            </span>
          </div>

          <!-- Campo de busca -->
          <div class="produto-search-wrap">
            <i class="fas fa-search produto-search-icon"></i>
            <input
              type="text"
              class="produto-search-input"
              placeholder="Digite ao menos 3 letras..."
              v-model="produtoSearchQuery"
              @input="onProdutoSearchInput"
            />
            <button
              v-if="produtoSearchQuery"
              class="produto-search-clear"
              type="button"
              @click="clearProdutoSearch"
              title="Limpar"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>

          <!-- Estados -->
          <div v-if="produtoSearchLoading" class="d-flex align-items-center justify-content-center py-4 gap-2">
            <div class="spinner-border spinner-border-sm spinner-teal"></div>
            <span class="text-sm text-secondary">Buscando...</span>
          </div>

          <div v-else-if="produtoSearchError" class="alert alert-danger py-2 my-2">
            <i class="fas fa-exclamation-triangle me-2"></i>{{ produtoSearchError }}
          </div>

          <div v-else-if="produtoSearchQuery.trim().length > 0 && produtoSearchQuery.trim().length < 3" class="busca-hint">
            <i class="fas fa-keyboard mb-2"></i>
            <p class="mb-0 text-sm">Digite ao menos 3 letras para buscar</p>
          </div>

          <div v-else-if="produtoSearchQuery.trim().length >= 3 && produtoSearchResults.length === 0" class="busca-hint">
            <i class="fas fa-box-open mb-2"></i>
            <p class="mb-0 text-sm">Nenhum produto encontrado para "{{ produtoSearchQuery }}"</p>
          </div>

          <div v-else-if="produtoSearchResults.length > 0" class="cosmos-list">
            <p class="cosmos-count">{{ produtoSearchResults.length }} produto(s) encontrado(s) — selecione o correto</p>
            <label
              v-for="p in produtoSearchResults"
              :key="p.id"
              class="cosmos-row"
              :class="{ 'cosmos-row--selected': selectedProdutoBusca === p }"
            >
              <input type="radio" :value="p" v-model="selectedProdutoBusca" class="cosmos-radio" />
              <img
                v-if="p.image || p.image_url"
                :src="p.image || p.image_url"
                class="busca-thumb"
                :alt="p.description"
                @error="$event.target.style.display='none'"
              />
              <div v-else class="busca-thumb busca-thumb--empty">
                <i class="fas fa-box-open"></i>
              </div>
              <div class="cosmos-row-info">
                <span class="cosmos-row-desc">{{ p.description || p.name || 'Sem descrição' }}</span>
                <span class="cosmos-row-gtin">
                  <i class="fas fa-barcode me-1"></i>{{ p.gtin || 'Sem GTIN' }}
                </span>
              </div>
            </label>
          </div>
        </div>
        <div class="modal-box__footer modal-box__footer--resultado">
          <button class="btn-recusar" @click="closeBuscarProduto" :disabled="savingProdutoBusca">
            <i class="fas fa-times me-1"></i>Cancelar
          </button>
          <button
            class="btn-salvar-cosmos"
            @click="salvarProdutoBusca"
            :disabled="savingProdutoBusca || !selectedProdutoBusca"
          >
            <span v-if="savingProdutoBusca">
              <i class="fas fa-circle-notch fa-spin me-1"></i>Salvando...
            </span>
            <span v-else>
              <i class="fas fa-check me-1"></i>Salvar
            </span>
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { apiRequest, handleApiResponse, API_ENDPOINTS } from '@/config/api.js';
import { showErrorAlert, showSuccessAlert, showConfirmAlert } from '@/utils/swal.js';

export default {
  name: 'FilaFolhetosTable',

  data() {
    return {
      items: [],
      loading: false,
      error: null,
      supermercados: [],
      selectedSupermercadoId: '',
      expiresAt: '',
      loadingSupermercados: false,
      savingIds: new Set(),
      deletingIds: new Set(),
      modalRefazer: false,
      modalRefazerItem: null,
      modalTokens: [],
      loadingTokens: false,
      selectedToken: null,
      searchingGtin: false,
      modalResultado: false,
      cosmosProducts: [],
      selectedCosmosProduct: null,
      savingNewProduct: false,
      // Buscar produto cadastrado
      modalBuscarProduto: false,
      buscarProdutoItem: null,
      produtoSearchQuery: '',
      produtoSearchResults: [],
      produtoSearchLoading: false,
      produtoSearchError: null,
      produtoSearchDebounce: null,
      selectedProdutoBusca: null,
      savingProdutoBusca: false,
    };
  },

  computed: {
    cosmosGtin() {
      return this.selectedCosmosProduct?.gtin ?? null;
    },
    selectedSupermercadoNome() {
      const s = this.supermercados.find(
        (s) => String(s.id) === String(this.selectedSupermercadoId)
      );
      return s ? (s.nome || s.name) : '';
    },
  },

  watch: {
    modalRefazer(val) {
      document.body.style.overflow = val ? 'hidden' : '';
    },
    modalResultado(val) {
      document.body.style.overflow = val ? 'hidden' : '';
    },
    modalBuscarProduto(val) {
      document.body.style.overflow = val ? 'hidden' : '';
    },
  },

  beforeUnmount() {
    document.body.style.overflow = '';
  },

  mounted() {
    this.loadSupermercados();
    this.load();
  },

  methods: {
    async loadSupermercados() {
      this.loadingSupermercados = true;
      try {
        const response = await apiRequest(API_ENDPOINTS.SUPERMARKET);
        const data = await handleApiResponse(response);
        this.supermercados = Array.isArray(data) ? data : (data.data || []);
      } catch (err) {
        console.error('Erro ao carregar supermercados:', err.message);
      } finally {
        this.loadingSupermercados = false;
      }
    },

    async load() {
      this.loading = true;
      this.error = null;
      try {
        const response = await apiRequest(API_ENDPOINTS.FLYER_PRODUCTS);
        const data = await handleApiResponse(response);
        const raw = Array.isArray(data) ? data : (data.products || data.data || []);
        this.items = raw.map(item => ({
          ...item,
          editPrice: item.price ? String(item.price) : '',
        }));
      } catch (err) {
        this.error = err.message;
      } finally {
        this.loading = false;
      }
    },

    async saveProduct(item) {
      const idProduct = item.id_product || item.id;
      const price = parseFloat(item.editPrice);

      if (!idProduct || isNaN(price) || price <= 0) {
        showErrorAlert('Dados inválidos', 'Verifique o preço antes de salvar.');
        return;
      }

      this.savingIds = new Set([...this.savingIds, item.id]);

      try {
        await handleApiResponse(
          await apiRequest(API_ENDPOINTS.PRICE, {
            method: 'POST',
            body: JSON.stringify({
              id_supermarket: Number(this.selectedSupermercadoId),
              id_product: Number(idProduct),
              price,
              ...(this.expiresAt && { expires_at: this.expiresAt }),
            }),
          })
        );

        await handleApiResponse(
          await apiRequest(API_ENDPOINTS.FLYER_PRODUCTS_PROCESSED(item.id), {
            method: 'PATCH',
          })
        );

        this.items = this.items.filter(i => i.id !== item.id);
      } catch (err) {
        showErrorAlert('Erro ao salvar', err.message);
      } finally {
        const next = new Set(this.savingIds);
        next.delete(item.id);
        this.savingIds = next;
      }
    },

    async openRefazerModal(item) {
      this.modalRefazerItem = item;
      this.modalRefazer = true;
      this.modalTokens = [];
      this.selectedToken = null;
      this.loadingTokens = true;
      try {
        const response = await apiRequest(API_ENDPOINTS.TOKENS);
        const data = await handleApiResponse(response);
const list = Array.isArray(data) ? data
          : Array.isArray(data?.data) ? data.data
          : Array.isArray(data?.tokens) ? data.tokens
          : [];
        this.modalTokens = list;
      } catch (err) {
        console.error('Erro ao carregar tokens:', err.message);
      } finally {
        this.loadingTokens = false;
      }
    },

    async confirmDelete(item) {
      const result = await showConfirmAlert(
        'Excluir produto',
        'Deseja remover este produto da fila?',
        'Sim, excluir',
        'Cancelar'
      );
      if (!result.isConfirmed) return;

      this.deletingIds = new Set([...this.deletingIds, item.id]);
      try {
        await handleApiResponse(
          await apiRequest(API_ENDPOINTS.FLYER_PRODUCTS_DEACTIVATE(item.id), {
            method: 'PATCH',
          })
        );
        this.items = this.items.filter(i => i.id !== item.id);
      } catch (err) {
        showErrorAlert('Erro ao excluir', err.message);
      } finally {
        const next = new Set(this.deletingIds);
        next.delete(item.id);
        this.deletingIds = next;
      }
    },

    async buscarGtin() {
      if (!this.selectedToken || !this.modalRefazerItem) return;
      this.searchingGtin = true;
      try {
        const data = await handleApiResponse(
          await apiRequest(API_ENDPOINTS.COSMOS_SEARCH, {
            method: 'POST',
            body: JSON.stringify({
              token: this.selectedToken.token,
              description: this.modalRefazerItem.description || this.modalRefazerItem.name,
            }),
          })
        );
        const products = data.products || (Array.isArray(data) ? data : []);
        if (products.length === 0) {
          showErrorAlert('Nenhum produto encontrado', 'Tente uma descrição diferente.');
          return;
        }
        this.cosmosProducts = products;
        this.selectedCosmosProduct = null;
        this.modalRefazer = false;
        this.modalResultado = true;
      } catch (err) {
        showErrorAlert('Erro ao buscar GTIN', err.message);
      } finally {
        this.searchingGtin = false;
      }
    },

    async salvarNovoProduto() {
      const item = this.modalRefazerItem;
      const gtin = this.cosmosGtin;
      const price = parseFloat(item.editPrice);

      if (!gtin) {
        showErrorAlert('GTIN não encontrado', 'Selecione um produto da lista.');
        return;
      }
      if (!this.selectedSupermercadoId || isNaN(price) || price <= 0) {
        showErrorAlert('Dados inválidos', 'Verifique o supermercado e o preço no card.');
        return;
      }
      if (!this.selectedToken) {
        showErrorAlert('Token não selecionado', 'Selecione um token antes de salvar o produto.');
        return;
      }

      this.savingNewProduct = true;
      try {
        const productData = await handleApiResponse(
          await apiRequest(API_ENDPOINTS.PRODUCT, {
            method: 'POST',
            body: JSON.stringify({ gtin, token: this.selectedToken.token }),
          })
        );

        const idProduct = productData.id || productData.id_product || productData.data?.id;

        await handleApiResponse(
          await apiRequest(API_ENDPOINTS.PRICE, {
            method: 'POST',
            body: JSON.stringify({
              id_supermarket: Number(this.selectedSupermercadoId),
              id_product: Number(idProduct),
              price,
              ...(this.expiresAt && { expires_at: this.expiresAt }),
            }),
          })
        );

        await handleApiResponse(
          await apiRequest(API_ENDPOINTS.FLYER_PRODUCTS_PROCESSED(item.id), {
            method: 'PATCH',
          })
        );

        this.items = this.items.filter(i => i.id !== item.id);
        this.modalResultado = false;
        this.cosmosProducts = [];
        this.selectedCosmosProduct = null;
        this.modalRefazerItem = null;
        showSuccessAlert('Produto salvo!', 'Produto cadastrado e vinculado ao supermercado.');
      } catch (err) {
        showErrorAlert('Erro ao salvar produto', err.message);
      } finally {
        this.savingNewProduct = false;
      }
    },

    recusarProduto() {
      this.modalResultado = false;
      this.cosmosProducts = [];
      this.selectedCosmosProduct = null;
    },

    // ── Buscar produto cadastrado ──
    openBuscarProdutoModal(item) {
      this.buscarProdutoItem = item;
      this.produtoSearchResults = [];
      this.selectedProdutoBusca = null;
      this.produtoSearchError = null;
      // Pré-preenche com a descrição do folheto e já dispara a busca
      this.produtoSearchQuery = item.description || item.name || '';
      this.modalBuscarProduto = true;
      if (this.produtoSearchQuery.trim().length >= 3) {
        this.runProdutoSearch();
      }
    },

    onProdutoSearchInput() {
      clearTimeout(this.produtoSearchDebounce);
      this.selectedProdutoBusca = null;
      const q = this.produtoSearchQuery.trim();

      if (q.length < 3) {
        this.produtoSearchResults = [];
        this.produtoSearchLoading = false;
        this.produtoSearchError = null;
        return;
      }

      // Busca a partir da 3ª letra, com debounce
      this.produtoSearchDebounce = setTimeout(() => this.runProdutoSearch(), 400);
    },

    async runProdutoSearch() {
      const q = this.produtoSearchQuery.trim();
      if (q.length < 3) return;

      this.produtoSearchLoading = true;
      this.produtoSearchError = null;

      try {
        const data = await handleApiResponse(
          await apiRequest(`${API_ENDPOINTS.PRODUCT_SEARCH}?q=${encodeURIComponent(q)}`)
        );
        this.produtoSearchResults = Array.isArray(data)
          ? data
          : (data.data || data.produtos || data.products || []);
      } catch (err) {
        this.produtoSearchError = err.message;
        this.produtoSearchResults = [];
      } finally {
        this.produtoSearchLoading = false;
      }
    },

    clearProdutoSearch() {
      clearTimeout(this.produtoSearchDebounce);
      this.produtoSearchQuery = '';
      this.produtoSearchResults = [];
      this.selectedProdutoBusca = null;
      this.produtoSearchError = null;
    },

    async salvarProdutoBusca() {
      const item = this.buscarProdutoItem;
      const produto = this.selectedProdutoBusca;
      const price = parseFloat(item?.editPrice);

      if (!produto) {
        showErrorAlert('Selecione um produto', 'Escolha um produto da lista antes de salvar.');
        return;
      }
      if (!this.selectedSupermercadoId || isNaN(price) || price <= 0) {
        showErrorAlert('Dados inválidos', 'Verifique o supermercado e o preço no card.');
        return;
      }

      const idProduct = produto.id || produto.id_product;
      this.savingProdutoBusca = true;

      try {
        // Salva o preço do folheto vinculado ao supermercado selecionado
        await handleApiResponse(
          await apiRequest(API_ENDPOINTS.PRICE, {
            method: 'POST',
            body: JSON.stringify({
              id_supermarket: Number(this.selectedSupermercadoId),
              id_product: Number(idProduct),
              price,
              ...(this.expiresAt && { expires_at: this.expiresAt }),
            }),
          })
        );

        // Marca o item da fila como processado
        await handleApiResponse(
          await apiRequest(API_ENDPOINTS.FLYER_PRODUCTS_PROCESSED(item.id), {
            method: 'PATCH',
          })
        );

        this.items = this.items.filter((i) => i.id !== item.id);
        this.closeBuscarProduto();
        showSuccessAlert('Produto salvo!', 'Preço vinculado ao supermercado.');
      } catch (err) {
        showErrorAlert('Erro ao salvar', err.message);
      } finally {
        this.savingProdutoBusca = false;
      }
    },

    closeBuscarProduto() {
      clearTimeout(this.produtoSearchDebounce);
      this.modalBuscarProduto = false;
      this.buscarProdutoItem = null;
      this.produtoSearchQuery = '';
      this.produtoSearchResults = [];
      this.selectedProdutoBusca = null;
      this.produtoSearchError = null;
    },

    async copyGtin(gtin) {
      try {
        await navigator.clipboard.writeText(String(gtin));
      } catch {
        const el = document.createElement('input');
        el.value = String(gtin);
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
    },

    // Considera válido apenas preço numérico maior que zero
    hasValidPrice(item) {
      const price = parseFloat(item?.editPrice);
      return !isNaN(price) && price > 0;
    },

    formatDate(value) {
      if (!value) return '—';
      const d = new Date(value);
      if (isNaN(d)) return value;
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
  },
};
</script>

<style scoped>
/* ── Card wrapper ── */
.fila-folhetos-card {
  border: none;
  border-radius: 1rem;
}

/* ── Supermarket bar ── */
.supermarket-bar { max-width: 360px; }

.form-label-custom {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
  margin-bottom: 6px;
  display: block;
}

.form-select-custom {
  border: 1.5px solid #e9ecef;
  border-radius: 0.75rem;
  padding: 0.55rem 1rem;
  font-size: 0.875rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-select-custom:focus {
  border-color: #17c1e8;
  box-shadow: 0 0 0 3px rgba(23,193,232,0.15);
  outline: none;
}

/* ── Refresh ── */
.btn-refresh {
  background: transparent;
  border: 1.5px solid #e0e0e0;
  border-radius: 0.6rem;
  color: #6c757d;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}
.btn-refresh:hover:not(:disabled) { border-color: #17c1e8; color: #17c1e8; }
.btn-refresh:disabled { opacity: 0.5; cursor: not-allowed; }

.spinner-teal { color: #17c1e8; width: 1.5rem; height: 1.5rem; }

/* ── Empty ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: #ced4da;
  font-size: 2.5rem;
}

/* ── Grid ── */
.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 16px;
  align-items: start;
}

/* ── Product card ── */
.pcard {
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  border: 1.5px solid #eef0f4;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s, transform 0.2s, border-color 0.2s;
}

.pcard:hover {
  border-color: rgba(23,193,232,0.35);
  box-shadow: 0 6px 24px rgba(23,193,232,0.10);
  transform: translateY(-2px);
}

/* Botão excluir */
.pcard__delete-btn {
  position: absolute;
  top: 7px;
  right: 7px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: rgba(234, 6, 6, 0.08);
  color: #ea0606;
  font-size: 0.62rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s;
  z-index: 2;
}

.pcard__delete-btn:hover:not(:disabled) {
  background: rgba(234, 6, 6, 0.18);
  transform: scale(1.1);
}

.pcard__delete-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Zona 1 — imagem */
.pcard__thumb {
  height: 110px;
  background: linear-gradient(135deg, #f5f7fa 0%, #eef0f4 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.pcard__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pcard__no-img {
  font-size: 2.2rem;
  color: #ced4da;
}

/* Zona 2 — info */
.pcard__info {
  padding: 12px 12px 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-bottom: 1px solid #f3f4f6;
}

.pcard__name {
  font-size: 0.8rem;
  font-weight: 700;
  color: #2d3748;
  line-height: 1.35;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  height: 2.7em;
}

.pcard__meta {
  display: flex;
  align-items: center;
  gap: 5px;
  height: 18px;
}

.pcard__brand {
  font-size: 0.68rem;
  font-weight: 700;
  color: #17c1e8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 50%;
}

.pcard__dot {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #cbd5e0;
  flex-shrink: 0;
}

.pcard__weight {
  font-size: 0.68rem;
  color: #a0aec0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pcard__gtin {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.65rem;
  color: #b0bac9;
  font-family: monospace;
  height: 16px;
}

.pcard__gtin i { font-size: 0.6rem; }

/* Zona 3 — preço */
.pcard__price-zone {
  padding: 10px 12px 8px;
  border-bottom: 1px solid #f3f4f6;
}

.pcard__price-label {
  display: block;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #a0aec0;
  margin-bottom: 5px;
}

.pcard__price-input {
  display: flex;
  align-items: center;
  background: #f7fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.pcard__price-input:focus-within {
  border-color: #17c1e8;
  box-shadow: 0 0 0 3px rgba(23,193,232,0.12);
  background: #fff;
}

.pcard__currency {
  padding: 6px 8px;
  font-size: 0.72rem;
  font-weight: 800;
  color: #718096;
  background: #edf2f7;
  border-right: 1.5px solid #e2e8f0;
  flex-shrink: 0;
}

.pcard__price-input input {
  border: none;
  outline: none;
  background: transparent;
  padding: 6px 8px;
  font-size: 0.85rem;
  font-weight: 700;
  color: #2d3748;
  width: 100%;
}

.pcard__price-input--invalid {
  border-color: #ea0606;
  background: rgba(234, 6, 6, 0.04);
}

.pcard__price-warning {
  display: flex;
  align-items: center;
  margin: 6px 0 0;
  font-size: 0.65rem;
  font-weight: 600;
  color: #ea0606;
  line-height: 1.3;
}

.pcard__price-warning i { font-size: 0.62rem; }

/* Zona 4 — rodapé */
.pcard__footer {
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}

.pcard__date {
  font-size: 0.65rem;
  color: #b0bac9;
  white-space: nowrap;
}

.pcard__save-btn {
  padding: 4px 12px;
  border: none;
  border-radius: 20px;
  background: linear-gradient(310deg, #17c1e8 0%, #20c997 100%);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
}

.pcard__save-btn:hover:not(:disabled) {
  box-shadow: 0 3px 10px rgba(23,193,232,0.4);
  transform: translateY(-1px);
}

.pcard__save-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* ── Refazer button ── */
.pcard__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
}

/* ── Buscar produto button ── */
.pcard__find-btn {
  padding: 4px 10px;
  border: 1.5px solid #e2e8f0;
  border-radius: 20px;
  background: transparent;
  color: #718096;
  font-size: 0.68rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
}

.pcard__find-btn:hover:not(:disabled) {
  border-color: #20c997;
  color: #20c997;
  background: rgba(32,201,151,0.06);
}

.pcard__find-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pcard__redo-btn {
  padding: 4px 10px;
  border: 1.5px solid #e2e8f0;
  border-radius: 20px;
  background: transparent;
  color: #718096;
  font-size: 0.68rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
}

.pcard__redo-btn:hover:not(:disabled) {
  border-color: #17c1e8;
  color: #17c1e8;
  background: rgba(23,193,232,0.06);
}

.pcard__redo-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ── Modal ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(2px);
}

.modal-box {
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 420px;
  margin: 1rem;
  box-shadow: 0 20px 60px rgba(0,0,0,0.15);
  overflow: hidden;
}

.modal-box__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.modal-close {
  background: none;
  border: none;
  color: #a0aec0;
  font-size: 1rem;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s;
}

.modal-close:hover { color: #344767; }

.modal-box__body {
  padding: 24px 20px;
  max-height: 60vh;
  overflow-y: auto;
}

/* ── Tokens list ── */
.tokens-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.token-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f7fafc;
  border: 1.5px solid #eef0f4;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}

.token-row:hover {
  border-color: rgba(23,193,232,0.4);
  background: rgba(23,193,232,0.04);
}

.token-row--selected {
  border-color: #17c1e8;
  background: rgba(23,193,232,0.08);
}

.token-radio {
  accent-color: #17c1e8;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: pointer;
}

.token-name {
  flex: 1;
}

.token-name {
  font-size: 0.82rem;
  font-weight: 600;
  color: #344767;
}

.token-qty {
  font-size: 0.82rem;
  font-weight: 800;
  color: #17c1e8;
  background: rgba(23,193,232,0.08);
  border-radius: 20px;
  padding: 2px 12px;
  min-width: 48px;
  text-align: center;
}

/* ── Footer total ── */
.footer-info {
  padding-top: 0.75rem;
  border-top: 1px solid #f0f0f0;
  margin-top: 1rem;
}

/* ── Modal footer ── */
.modal-box__footer {
  padding: 14px 20px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
}

.modal-box__footer--resultado {
  gap: 10px;
}

/* ── Buscar GTIN button ── */
.btn-buscar-gtin {
  padding: 8px 20px;
  border: none;
  border-radius: 20px;
  background: linear-gradient(310deg, #17c1e8 0%, #20c997 100%);
  color: #fff;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
}

.btn-buscar-gtin:hover:not(:disabled) {
  box-shadow: 0 3px 10px rgba(23,193,232,0.4);
  transform: translateY(-1px);
}

.btn-buscar-gtin:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* ── Cosmos result modal ── */
.modal-box--resultado { max-width: 560px; }

.cosmos-count {
  font-size: 0.78rem;
  color: #a0aec0;
  margin-bottom: 12px;
}

.cosmos-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cosmos-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f7fafc;
  border: 1.5px solid #eef0f4;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}

.cosmos-row:hover {
  border-color: rgba(23,193,232,0.4);
  background: rgba(23,193,232,0.04);
}

.cosmos-row--selected {
  border-color: #17c1e8;
  background: rgba(23,193,232,0.08);
}

.cosmos-radio {
  accent-color: #17c1e8;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: pointer;
}

.cosmos-barcode-img {
  width: 90px;
  height: 60px;
  object-fit: contain;
  flex-shrink: 0;
  border-radius: 8px;
  background: #fff;
  border: 1.5px solid #eef0f4;
  padding: 4px;
}

.cosmos-row-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.cosmos-row-desc {
  font-size: 0.82rem;
  font-weight: 600;
  color: #2d3748;
  line-height: 1.4;
  white-space: normal;
}

.cosmos-row-gtin {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.72rem;
  color: #17c1e8;
  font-family: monospace;
}

.cosmos-copy-btn {
  background: none;
  border: none;
  padding: 0 2px;
  cursor: pointer;
  color: #a0aec0;
  font-size: 0.7rem;
  line-height: 1;
  transition: color 0.15s;
}

.cosmos-copy-btn:hover { color: #17c1e8; }

/* ── Resultado action buttons ── */
.btn-recusar {
  padding: 8px 20px;
  border: 1.5px solid #e2e8f0;
  border-radius: 20px;
  background: transparent;
  color: #718096;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-recusar:hover:not(:disabled) {
  border-color: #ea0606;
  color: #ea0606;
}

.btn-recusar:disabled { opacity: 0.4; cursor: not-allowed; }

.btn-salvar-cosmos {
  padding: 8px 20px;
  border: none;
  border-radius: 20px;
  background: linear-gradient(310deg, #17c1e8 0%, #20c997 100%);
  color: #fff;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-salvar-cosmos:hover:not(:disabled) {
  box-shadow: 0 3px 10px rgba(23,193,232,0.4);
  transform: translateY(-1px);
}

.btn-salvar-cosmos:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* ── Buscar produto modal ── */
.busca-resumo {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}

.busca-resumo__item {
  font-size: 0.72rem;
  font-weight: 700;
  color: #718096;
  background: #f7fafc;
  border: 1.5px solid #eef0f4;
  border-radius: 20px;
  padding: 4px 12px;
}

.busca-resumo__price {
  color: #20c997;
  background: rgba(32,201,151,0.08);
  border-color: rgba(32,201,151,0.25);
}

.produto-search-wrap {
  position: relative;
  margin-bottom: 14px;
}

.produto-search-icon {
  position: absolute;
  top: 50%;
  left: 14px;
  transform: translateY(-50%);
  font-size: 0.8rem;
  color: #adb5bd;
  pointer-events: none;
}

.produto-search-input {
  width: 100%;
  height: 40px;
  padding: 0 38px 0 36px;
  font-size: 0.875rem;
  color: #495057;
  background-color: #f8f9fa;
  border: 1.5px solid #e9ecef;
  border-radius: 20px;
  outline: none;
  transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
}

.produto-search-input::placeholder { color: #adb5bd; }

.produto-search-input:focus {
  background-color: #fff;
  border-color: #20c997;
  box-shadow: 0 0 0 3px rgba(32,201,151,0.15);
}

.produto-search-clear {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #adb5bd;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.produto-search-clear:hover {
  background-color: #e9ecef;
  color: #495057;
}

.busca-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  color: #cbd5e0;
  text-align: center;
}

.busca-hint i { font-size: 1.6rem; }
.busca-hint p { color: #a0aec0; }

.busca-thumb {
  width: 52px;
  height: 52px;
  object-fit: cover;
  flex-shrink: 0;
  border-radius: 8px;
  background: #fff;
  border: 1.5px solid #eef0f4;
}

.busca-thumb--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cbd5e0;
  font-size: 1.3rem;
}

@media (max-width: 576px) {
  .products-grid { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 380px) {
  .products-grid { grid-template-columns: 1fr; }
}
</style>
